/* eslint-disable max-lines */

import { EnvironmentState, EnvironmentRequest } from 'vrlcm-gui-types';
import * as Constants from '../constants/action-constants';
import { EnvironmentItems } from '../types/Environments';
import { UpgradeState } from '../types/workflows/UpgradeWorkflow';
import { CLEAR_ENVIRONMENTS } from '../constants/action-constants';

export interface PollEnvironmentsStartOpts {
  count?: number;
  fetchMapData?: boolean;
  isCloudProxyEnvironment?: boolean;
  skipWaitForDatacenters?: boolean;
}

export interface PollEnvironmentsStart {
  type: Constants.POLL_ENVIRONMENTS_START;
  payload: {
    opts?: PollEnvironmentsStartOpts;
  };
}

export interface PollEnvironments {
  type: Constants.POLL_ENVIRONMENTS;
}

export interface PollEnvironmentsSuccess {
  type: Constants.POLL_ENVIRONMENTS_SUCCESS;
  payload: {
    environments: EnvironmentItems;
    fetchMapData?: boolean;
  };
}

export interface PollEnvironmentsFailed {
  type: Constants.POLL_ENVIRONMENTS_FAILED;
}

export interface PollEnvironmentsStop {
  type: Constants.POLL_ENVIRONMENTS_STOP;
}

export interface ClearEnvironments {
  type: Constants.CLEAR_ENVIRONMENTS;
}

export interface DeleteEnvironment {
  type: Constants.DELETE_ENVIRONMENT;
  payload: {
    environmentId: string;
    state: 'completed' | 'pending';
    deleteFromInventory: boolean;
  };
}

export interface DeleteProduct {
  type: Constants.DELETE_PRODUCT;
  payload: {
    environmentId: string;
    productId: string;
  };
}

export interface GenerateScaleOutRequest {
  type: Constants.GENERATE_SCALE_OUT_REQUEST;
  payload: {
    updatedEnvironment: EnvironmentState;
    scaleOutState: EnvironmentState;
  };
}

export interface GenerateScaleOutRequestSuccess {
  type: Constants.GENERATE_SCALE_OUT_REQUEST_SUCCESS;
  payload: {
    state: EnvironmentState;
  };
}

export interface GenerateScaleOutRequestFailed {
  type: Constants.GENERATE_SCALE_OUT_REQUEST_FAILED;
  payload: {
    errorStatus: number;
  };
}

export interface ResetStatus {
  type: Constants.RESET_STATUS;
}

export interface GenerateOrganicGrowthRequest {
  type: Constants.GENERATE_ORGANIC_GROWTH_REQUEST;
  payload: {
    updatedEnvironment: EnvironmentState;
    organicGrowthState: EnvironmentState;
  };
}

export interface GenerateOrganicGrowthRequestSuccess {
  type: Constants.GENERATE_ORGANIC_GROWTH_REQUEST_SUCCESS;
  payload: {
    state: EnvironmentState;
  };
}

export interface GenerateOrganicGrowthRequestFailed {
  type: Constants.GENERATE_ORGANIC_GROWTH_REQUEST_FAILED;
}

export interface GenerateUpgradeProductRequest {
  type: Constants.GENERATE_UPGRADE_PRODUCT_REQUEST;
  payload: {
    updatedEnvironment: EnvironmentState;
    upgradeState: UpgradeState;
  };
}

export interface GenerateUpgradeProductRequestSuccess {
  type: Constants.GENERATE_UPGRADE_PRODUCT_REQUEST_SUCCESS;
  payload: {
    state: UpgradeState;
  };
}

export interface GenerateUpgradeProductRequestFailed {
  type: Constants.GENERATE_UPGRADE_PRODUCT_REQUEST_FAILED;
}

export interface DownloadEnvironmentLogsRequest {
  type: Constants.DOWNLOAD_ENVIRONMENT_LOGS_REQUEST;
  payload: {
    environmentId: string;
  };
}

export interface ExportConfiguration {
  type: Constants.EXPORT_CONFIGURATION;
  payload: {
    environmentId: string;
    type: string;
  };
}

export interface ExportConfigurationSuccess {
  type: Constants.EXPORT_CONFIGURATION_SUCCESS;
  payload: {
    environmentId: string;
  };
}

export interface ExportProductConfiguration {
  type: Constants.EXPORT_PRODUCT_CONFIGURATION;
  payload: {
    environmentId: string;
    productId: string;
    type: string;
    tenantId?: string;
  };
}

export interface ExportProductConfigurationSuccess {
  type: Constants.EXPORT_PRODUCT_CONFIGURATION_SUCCESS;
  payload: {
    environmentId: string;
    productId: string;
  };
}

export interface TriggerInventorySync {
  type: Constants.TRIGGER_INVENTORY_SYNC;
}

export interface TriggerEnvironmentInventorySync {
  type: Constants.TRIGGER_ENVIRONMENT_INVENTORY_SYNC;
  payload: {
    environmentId: string;
  };
}

export interface TriggerProductInventorySync {
  type: Constants.TRIGGER_PRODUCT_INVENTORY_SYNC;
  payload: {
    environmentId: string;
    productId: string;
    tenantId?: string;
  };
}

export interface ToggleHealth {
  type: Constants.TOGGLE_HEALTH;
  payload: {
    environmentId: string;
    status: 'enable' | 'disable';
  };
}

export interface RenameEnvironment {
  type: Constants.RENAME_ENVIRONMENT;
  payload: {
    environmentId: string;
    environmentName: string;
    environmentDescription?: string | null;
  };
}

export interface UpdateEnvironment {
  type: Constants.UPDATE_ENVIRONMENT;
  payload: {
    environmentId: string;
  };
}

export interface UpdateEnvironmentSuccess {
  type: Constants.UPDATE_ENVIRONMENT_SUCCESS;
  payload: {
    environment: EnvironmentRequest;
  };
}

export interface UpdateVidmClusterHealthCheckStatus {
  type: Constants.UPDATE_CLUSTER_HEALTH_CHECK_STATUS;
  payload: {
    environmentId: string;
    productId: string;
    status: string;
  };
}

export type EnvironmentsAction =
  | PollEnvironmentsStart
  | PollEnvironments
  | PollEnvironmentsSuccess
  | PollEnvironmentsFailed
  | PollEnvironmentsStop
  | ClearEnvironments
  | DeleteEnvironment
  | DeleteProduct
  | GenerateScaleOutRequest
  | GenerateScaleOutRequestSuccess
  | GenerateScaleOutRequestFailed
  | GenerateOrganicGrowthRequest
  | GenerateOrganicGrowthRequestSuccess
  | GenerateOrganicGrowthRequestFailed
  | GenerateUpgradeProductRequest
  | GenerateUpgradeProductRequestFailed
  | GenerateUpgradeProductRequestSuccess
  | DownloadEnvironmentLogsRequest
  | ExportConfiguration
  | ExportConfigurationSuccess
  | ExportProductConfiguration
  | ExportProductConfigurationSuccess
  | TriggerInventorySync
  | TriggerEnvironmentInventorySync
  | TriggerProductInventorySync
  | ToggleHealth
  | RenameEnvironment
  | UpdateEnvironment
  | UpdateEnvironmentSuccess
  | UpdateVidmClusterHealthCheckStatus
  | ResetStatus;

export const pollEnvironmentsStart = (opts?: {
  count?: number;
  fetchMapData?: boolean;
  isCloudProxyEnvironment?: boolean;
  skipWaitForDatacenters?: boolean;
}): PollEnvironmentsStart => ({
  type: Constants.POLL_ENVIRONMENTS_START,
  payload: {
    opts
  }
});

export const pollEnvironments = (): PollEnvironments => ({
  type: Constants.POLL_ENVIRONMENTS
});

export const pollEnvironmentsSuccess = (
  environments: EnvironmentItems,
  fetchMapData?: boolean
): PollEnvironmentsSuccess => ({
  payload: { environments, fetchMapData },
  type: Constants.POLL_ENVIRONMENTS_SUCCESS
});

export const pollEnvironmentsFailed = (): PollEnvironmentsFailed => ({
  type: Constants.POLL_ENVIRONMENTS_FAILED
});

export const pollEnvironmentsStop = (): PollEnvironmentsStop => ({
  type: Constants.POLL_ENVIRONMENTS_STOP
});

export const clearEnvironments = (): ClearEnvironments => ({
  type: Constants.CLEAR_ENVIRONMENTS
});

export const deleteEnvironment = (
  environmentId: string,
  state: 'completed' | 'pending',
  deleteFromInventory: boolean
): DeleteEnvironment => ({
  type: Constants.DELETE_ENVIRONMENT,
  payload: { environmentId, state, deleteFromInventory }
});

export const deleteProduct = (
  environmentId: string,
  productId: string
): DeleteProduct => ({
  type: Constants.DELETE_PRODUCT,
  payload: { environmentId, productId }
});

export const generateScaleOutRequest = (
  updatedEnvironment: EnvironmentState,
  scaleOutState: EnvironmentState
): GenerateScaleOutRequest => ({
  type: Constants.GENERATE_SCALE_OUT_REQUEST,
  payload: { updatedEnvironment, scaleOutState }
});

export const generateScaleOutRequestSuccess = (
  state: EnvironmentState
): GenerateScaleOutRequestSuccess => ({
  type: Constants.GENERATE_SCALE_OUT_REQUEST_SUCCESS,
  payload: { state }
});

export const generateScaleOutRequestFailed = (
  errorStatus: number
): GenerateScaleOutRequestFailed => ({
  type: Constants.GENERATE_SCALE_OUT_REQUEST_FAILED,
  payload: { errorStatus }
});

export const resetStatus = (): ResetStatus => ({
  type: Constants.RESET_STATUS
});

export const generateOrganicGrowthRequest = (
  updatedEnvironment: EnvironmentState,
  organicGrowthState: EnvironmentState
): GenerateOrganicGrowthRequest => ({
  type: Constants.GENERATE_ORGANIC_GROWTH_REQUEST,
  payload: { updatedEnvironment, organicGrowthState }
});

export const generateOrganicGrowthRequestSuccess = (
  state: EnvironmentState
): GenerateOrganicGrowthRequestSuccess => ({
  type: Constants.GENERATE_ORGANIC_GROWTH_REQUEST_SUCCESS,
  payload: { state }
});

export const generateOrganicGrowthRequestFailed = (): GenerateOrganicGrowthRequestFailed => ({
  type: Constants.GENERATE_ORGANIC_GROWTH_REQUEST_FAILED
});

export const generateUpgradeProductRequest = (
  updatedEnvironment: EnvironmentState,
  upgradeState: UpgradeState
): GenerateUpgradeProductRequest => ({
  type: Constants.GENERATE_UPGRADE_PRODUCT_REQUEST,
  payload: { updatedEnvironment, upgradeState }
});

export const generateUpgradeProductRequestSuccess = (
  state: UpgradeState
): GenerateUpgradeProductRequestSuccess => ({
  type: Constants.GENERATE_UPGRADE_PRODUCT_REQUEST_SUCCESS,
  payload: { state }
});

export const generateUpgradeProductRequestFailed = (): GenerateUpgradeProductRequestFailed => ({
  type: Constants.GENERATE_UPGRADE_PRODUCT_REQUEST_FAILED
});

export const downloadEnvironmentLogsRequest = (
  environmentId: string
): DownloadEnvironmentLogsRequest => ({
  type: Constants.DOWNLOAD_ENVIRONMENT_LOGS_REQUEST,
  payload: { environmentId }
});

export const exportConfiguration = (
  environmentId: string,
  type: string
): ExportConfiguration => ({
  type: Constants.EXPORT_CONFIGURATION,
  payload: { environmentId, type }
});

export const exportConfigurationSuccess = (
  environmentId: string
): ExportConfigurationSuccess => ({
  type: Constants.EXPORT_CONFIGURATION_SUCCESS,
  payload: { environmentId }
});

export const exportProductConfiguration = (
  environmentId: string,
  productId: string,
  type: string,
  tenantId?: string
): ExportProductConfiguration => ({
  type: Constants.EXPORT_PRODUCT_CONFIGURATION,
  payload: { environmentId, productId, type, tenantId }
});

export const exportProductConfigurationSuccess = (
  environmentId: string,
  productId: string
): ExportProductConfigurationSuccess => ({
  type: Constants.EXPORT_PRODUCT_CONFIGURATION_SUCCESS,
  payload: { environmentId, productId }
});

export const triggerInventorySync = (): TriggerInventorySync => ({
  type: Constants.TRIGGER_INVENTORY_SYNC
});

export const triggerEnvironmentInventorySync = (
  environmentId: string
): TriggerEnvironmentInventorySync => ({
  type: Constants.TRIGGER_ENVIRONMENT_INVENTORY_SYNC,
  payload: { environmentId }
});

export const triggerProductInventorySync = (
  environmentId: string,
  productId: string,
  tenantId?: string
): TriggerProductInventorySync => ({
  type: Constants.TRIGGER_PRODUCT_INVENTORY_SYNC,
  payload: { environmentId, productId, tenantId }
});

export const toggleHealth = (
  environmentId: string,
  status: 'enable' | 'disable'
): ToggleHealth => ({
  type: Constants.TOGGLE_HEALTH,
  payload: { environmentId, status }
});

export const renameEnvironment = (
  environmentId: string,
  environmentName: string,
  environmentDescription: string
): RenameEnvironment => ({
  type: Constants.RENAME_ENVIRONMENT,
  payload: { environmentId, environmentName, environmentDescription }
});

export const updateEnvironment = (
  environmentId: string
): UpdateEnvironment => ({
  type: Constants.UPDATE_ENVIRONMENT,
  payload: { environmentId }
});

export const updateEnvironmentSuccess = (
  environment: EnvironmentRequest
): UpdateEnvironmentSuccess => ({
  type: Constants.UPDATE_ENVIRONMENT_SUCCESS,
  payload: { environment }
});

export const updateVidmClusterHealthCheckStatus = (
  environmentId: string,
  productId: string,
  status: string
): UpdateVidmClusterHealthCheckStatus => ({
  type: Constants.UPDATE_CLUSTER_HEALTH_CHECK_STATUS,
  payload: { environmentId, productId, status }
});
